import Vue from "vue";
import Vuex from "vuex";
import { Permission } from "@/services/Permissions";

Vue.use(Vuex);

interface AppState {
  checkingLogin: boolean;
  accessDenied: boolean;
  /* User */
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
  userAvatar: string | null;
  userGroups: string[] | null;
  userPermissions: string[] | null;
  isUserExternal: boolean | null;
  userTenantExists: boolean | null;
  mappedCustomer: [];
  /* Customer view switch */
  switchCustomerView: boolean | null;
  switchCustomerId: number | null;
  switchCustomerName: string | null;
  /* NavBar */
  isNavBarVisible: boolean;

  /* FooterBar */
  isFooterBarVisible: boolean;

  /* Aside */
  isAsideVisible: boolean;
  isAsideMobileExpanded: boolean;

  /* Dark mode */
  isDarkModeActive: boolean;
  countryList: [];
  schemeList: [];
  containerList: [];
  userList: [];
  customerList: [];
  treeKeyId: number;
  treeRule: object;
  properties: [];
  operators: [];
  compliancePageName: string | null;
  /* Error Handling */
  isBackendDown: boolean;
  isKeycloakDown: boolean;
  errorMessage: string | null;
}

export interface UserAppState {
  /* User */
  id: number | null;
  name: string | null;
  email: string | null;
  avatar: string | null;
  groups: string[] | null;
  permissions: string[] | null;
  isExternal: boolean | null;
  tenantExists?: boolean | null;
  mappedCustomer: [];
}

export interface CustomerSwitchState {
  /* Maintain state of internal users accessing customer view */
  isSwitchCustomerView: boolean | null;
  switchCustomerNumber: number | null;
  switchCustomerName: string | null;
}

interface GenericAppState {
  key: string;
  value: string | boolean | null;
}

export default new Vuex.Store({
  state: {
    checkingLogin: true,
    accessDenied: false,
    /* User */
    userId: null,
    userName: null,
    userEmail: null,
    userAvatar: null,
    userGroups: [],
    userPermissions: [],
    isUserExternal: null,
    userTenantExists: null,
    mappedCustomer: [],
    /* Customer view switch */
    switchCustomerView: null,
    switchCustomerId: null,
    switchCustomerName: null,
    /* NavBar */
    isNavBarVisible: true,

    /* FooterBar */
    isFooterBarVisible: true,

    /* Aside */
    isAsideVisible: true,
    isAsideMobileExpanded: false,

    /* Dark mode */
    isDarkModeActive: false,
    countryList: [],
    schemeList: [],
    containerList: [],
    customerList: [],
    userList: [],
    treeKeyId: 1,
    treeRule: {},
    properties: [],
    operators: [],
    compliancePageName: "Overview",
    /* Error Handling */
    isBackendDown: false,
    isKeycloakDown: false,
    errorMessage: null
  } as AppState,
  getters: {
    checkingLogin(state: AppState /*, getters*/) {
      return state.checkingLogin;
    },
    accessDenied(state: AppState /*, getters*/) {
      return state.accessDenied;
    },
    userId(state: AppState /*, getters*/) {
      return state.userId;
    },
    userName(state: AppState /*, getters*/) {
      return state.userName;
    },
    isUserExternal(state: AppState /*, getters*/) {
      return state.isUserExternal;
    },
    userTenantExists(state: AppState) {
      return state.userTenantExists;
    },
    mappedCustomer(state: AppState) {
      return state.mappedCustomer;
    },
    userPermissions(state: AppState /*, getters*/) {
      return state.userPermissions?.sort();
    },
    userHasPermission(state: AppState /*, getters*/) {
      return (perm: string) => {
        const hasPermission = !!state.userPermissions?.filter(p => p == perm)
          .length;
        // console.log(`Does ${state.userName} have ${perm}?`, hasPermission);
        return hasPermission;
      };
    },
    userGroups(state: AppState /*, getters*/) {
      return state.userGroups;
    },
    switchCustomerView(state: AppState /*, getters*/) {
      return state.switchCustomerView;
    },
    switchCustomerId(state: AppState /*, getters*/) {
      return state.switchCustomerId;
    },
    isBackendDown(state: AppState) {
      return state.isBackendDown;
    },
    isKeycloakDown(state: AppState) {
      return state.isKeycloakDown;
    },
    errorMessage(state: AppState) {
      return state.errorMessage;
    }
  },
  mutations: {
    /* A fit-them-all commit */
    basic(state: AppState, payload: GenericAppState) {
      if (payload.key in Object.keys(state)) {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        (state as any)[payload.key] = payload.value;
      }
    },

    /* User */
    user(state: AppState, payload: UserAppState) {
      if (payload.id) {
        state.userId = payload.id;
      }
      if (payload.name) {
        state.userName = payload.name;
      }
      if (payload.email) {
        state.userEmail = payload.email;
      }
      if (payload.avatar) {
        state.userAvatar = payload.avatar;
      }
      if (payload.isExternal) {
        state.isUserExternal = payload.isExternal;
      }
      if (typeof payload.tenantExists === "boolean") {
        state.userTenantExists = payload.tenantExists;
      }
      if (payload.mappedCustomer) {
        state.mappedCustomer = payload.mappedCustomer;
      }
      if (payload.groups) {
        state.userGroups = payload.groups;
      }
      if (payload.permissions) {
        state.userPermissions = payload.permissions;
        state.accessDenied =
          !payload.permissions ||
          !payload.permissions.indexOf ||
          payload.permissions.indexOf(Permission.BASIC_ACCESS) == -1;
      } else {
        state.accessDenied = true;
      }
      state.checkingLogin = false;
    },

    /* Customer switch state */
    customer(state: AppState, payload: CustomerSwitchState) {
      if (payload.isSwitchCustomerView) {
        state.switchCustomerView = payload.isSwitchCustomerView;
      }
      if (payload.switchCustomerNumber) {
        state.switchCustomerId = payload.switchCustomerNumber;
      }
      if (payload.switchCustomerName) {
        state.switchCustomerName = payload.switchCustomerName;
      }
    },

    /* Aside Mobile */
    asideMobileStateToggle(state: AppState, payload: boolean | null = null) {
      const htmlClassName = "has-aside-mobile-expanded";

      let isShow;

      if (payload !== null) {
        isShow = payload;
      } else {
        isShow = !state.isAsideMobileExpanded;
      }

      if (isShow) {
        document.documentElement.classList.add(htmlClassName);
      } else {
        document.documentElement.classList.remove(htmlClassName);
      }

      state.isAsideMobileExpanded = isShow;
    },

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    /* Dark Mode */ darkModeToggle(state: AppState, payload = null) {
      const htmlClassName = "is-dark-mode-active";

      state.isDarkModeActive = !state.isDarkModeActive;

      if (state.isDarkModeActive) {
        document.documentElement.classList.add(htmlClassName);
      } else {
        document.documentElement.classList.remove(htmlClassName);
      }
    },
    accessDenied(state: AppState) {
      state.checkingLogin = false;
      state.accessDenied = true;
    },
    setCountryList(state, country) {
      state.countryList = country;
    },
    setContainerList(state, container) {
      state.containerList = container;
    },
    setSchemeList(state, scheme) {
      state.schemeList = scheme;
    },
    setUserList(state, users) {
      state.userList = users;
    },
    setCustomerList(state, customers) {
      state.customerList = customers;
    },
    incrementTreeKeyId(state: AppState) {
      state.treeKeyId = state.treeKeyId + 1;
    },
    setTreeRule(state, tree) {
      state.treeRule = tree;
    },
    setCompliancePageName(state: AppState, pageName) {
      state.compliancePageName = pageName;
    },
    setBackendDown(
      state: AppState,
      payload: { isDown: boolean; message?: string }
    ) {
      state.isBackendDown = payload.isDown;
      if (payload.message) {
        state.errorMessage = payload.message;
      }
    },
    setKeycloakDown(
      state: AppState,
      payload: { isDown: boolean; message?: string }
    ) {
      state.isKeycloakDown = payload.isDown;
      if (payload.message) {
        state.errorMessage = payload.message;
      }
    },
    clearError(state: AppState) {
      state.isBackendDown = false;
      state.isKeycloakDown = false;
      state.errorMessage = null;
    }
  },
  actions: {
    async setCountryList({ commit }, data) {
      commit("setCountryList", data);
    },
    async setContainerList({ commit }, data) {
      commit("setContainerList", data);
    },
    async setSchemeList({ commit }, data) {
      commit("setSchemeList", data);
    },
    async setUserList({ commit }, data) {
      commit("setUserList", data);
    },
    async setCustomerList({ commit }, data) {
      commit("setCustomerList", data);
    }
  }
});
